import { useEffect, useMemo, useRef, useState } from 'react'
import { BURNED, TEAMS, POSITIONS } from '../lib/landingData'

/**
 * The mechanic, on screen. 32 teams down, 6 positions across — a real
 * manager's board from the 2025 simulation, filling in week by week.
 *
 * Scrubbable: drag the week slider and the board follows. It advances on its
 * own until you touch it, then it's yours.
 */
export default function BurnBoardDemo() {
  // opens on a board that already has a pattern, then keeps filling
  const [week, setWeek] = useState(3)
  const [auto, setAuto] = useState(true)
  const wrap = useRef<HTMLDivElement>(null)
  const [seen, setSeen] = useState(false)

  // don't start counting until it's actually on screen
  useEffect(() => {
    const el = wrap.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect() } },
      { threshold: 0.25 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!auto || !seen) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setWeek(10)
      return
    }
    const id = setInterval(() => {
      setWeek((w) => (w >= 10 ? 10 : w + 1))
    }, 900)
    return () => clearInterval(id)
  }, [auto, seen])

  const spent = useMemo(() => {
    let n = 0
    for (const t of TEAMS) {
      for (const p of POSITIONS) {
        const wk = BURNED[t]?.[p]
        if (wk && wk <= week) n++
      }
    }
    return n
  }, [week])

  return (
    <div className="board-demo" ref={wrap}>
      <div className="board-side">
        <h3>The board, ten weeks in</h3>
        <p className="board-sub">
          One manager's real season from the 2025 simulation. Orange is spent —
          and spent is forever.
        </p>
        <div className="board-count">
          <span className="board-count-n">{spent}</span>
          <span className="board-count-d">of 192 burned</span>
        </div>
        <div className="board-scrub">
          <label htmlFor="wk">Week</label>
          <input
            id="wk"
            type="range"
            min={1}
            max={10}
            value={week}
            onChange={(e) => { setAuto(false); setWeek(Number(e.target.value)) }}
          />
          <output>{week}</output>
        </div>
        <p className="board-legend">
          <span className="legend-swatch is-open" /> available
          <span className="legend-swatch is-spent" /> spent
        </p>
      </div>

      <div className="board-scroll">
        <table className="board-grid">
          <thead>
            <tr>
              <th className="board-corner"><span className="sr-only">Team</span></th>
              {POSITIONS.map((p) => (
                <th key={p}>{p === 'DEF' ? 'D/ST' : p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TEAMS.map((t) => (
              <tr key={t}>
                <th className="board-team">{t}</th>
                {POSITIONS.map((p) => {
                  const wk = BURNED[t]?.[p]
                  const burned = !!wk && wk <= week
                  const fresh = wk === week
                  return (
                    <td
                      key={p}
                      className={`board-cell${burned ? ' is-burned' : ''}${fresh ? ' is-fresh' : ''}`}
                      title={burned
                        ? `${t} ${p === 'DEF' ? 'D/ST' : p} — burned Week ${wk}`
                        : `${t} ${p === 'DEF' ? 'D/ST' : p} — available`}
                    >
                      {burned ? wk : ''}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
